/**
 * TOEFL 考试特定的题目类型定义
 */

import {
  QuestionType,
  createExamQuestionTypeDefinition,
  createFieldDefinition,
  type ExamSpecificQuestionTypeDefinition,
} from '@examflow/core';
import { validateToeflReadingQuestionFormat } from '../../questionFormatValidators';

/**
 * TOEFL 阅读题定义
 */
export const toeflReadingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'toefl_reading',
    QuestionType.SINGLE_CHOICE,
    'TOEFL 阅读理解题',
    {
      requiredStimulus: ['passage'],
      extraFields: {
        // 文章编号（2 篇文章，每篇 10 题）
        passageNumber: createFieldDefinition(
          'passageNumber',
          'number',
          true,
          '文章编号 (1-2)',
          {
            validator: (value) => {
              const num = Number(value);
              if (num < 1 || num > 2) {
                return 'TOEFL 阅读必须属于第 1-2 篇文章';
              }
              return true;
            },
          }
        ),
        // 具体题型
        questionFormat: createFieldDefinition(
          'questionFormat',
          'string',
          true,
          'TOEFL 阅读题型格式',
          {
            validator: (value) => {
              const validFormats = [
                'factual_information', // 事实信息题
                'negative_factual', // 否定事实信息题
                'inference', // 推断题
                'vocabulary', // 词汇题
                'reference', // 指代题
                'insertion', // 句子插入题
                'prose_summary', // 文章小结题
                'rhetorical_purpose', // 修辞目的题
              ];
              return validFormats.includes(value as string)
                ? true
                : `不支持的 TOEFL 阅读题型: ${value}，必须是以下之一: ${validFormats.join(', ')}`;
            },
          }
        ),
        // 材料字数
        wordCount: createFieldDefinition('wordCount', 'number', false, '阅读材料字数'),
      },

      constraints: {
        maxWordCount: 750, // TOEFL 学术文章约 700 词
      },

      customValidator: (question) => {
        const errors = [];

        // 验证阅读材料长度
        if (question.passage) {
          const wordCount = question.passage.split(/\s+/).length;
          if (wordCount > 750) {
            errors.push({
              field: 'passage',
              message: 'TOEFL 阅读材料不能超过 750 词',
              code: 'TOEFL_PASSAGE_TOO_LONG',
            });
          }
        }

        // 验证填写的字数不超过上限
        if (question.wordCount != null) {
          if (Number(question.wordCount) > 750) {
            errors.push({
              field: 'wordCount',
              message: 'TOEFL 阅读材料字数不能超过 750 词',
              code: 'TOEFL_WORD_COUNT_TOO_LONG',
            });
          }
        }

        errors.push(...validateToeflReadingQuestionFormat(question));

        return errors;
      },
    }
  );

/**
 * TOEFL 听力题定义
 */
export const toeflListeningDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'toefl_listening',
    QuestionType.SINGLE_CHOICE,
    'TOEFL 听力题',
    {
      requiredStimulus: ['audio'],
      extraFields: {
        // 听力材料类型（对话或讲座）
        materialType: createFieldDefinition(
          'materialType',
          'string',
          true,
          '听力材料类型',
          {
            validator: (value) => {
              const validTypes = [
                'conversation', // 校园对话
                'lecture', // 学术讲座
              ];
              return validTypes.includes(value as string)
                ? true
                : `不支持的 TOEFL 听力材料类型: ${value}，必须是以下之一: ${validTypes.join(', ')}`;
            },
          }
        ),
        // 具体题型
        questionFormat: createFieldDefinition(
          'questionFormat',
          'string',
          true,
          'TOEFL 听力题型格式',
          {
            validator: (value) => {
              const validFormats = [
                'gist_content', // 主旨内容题
                'gist_purpose', // 主旨目的题
                'detail', // 细节题
                'attitude', // 态度题
                'organization', // 组织结构题
                'inference', // 推断题
                'connecting_content', // 信息关联题
              ];
              return validFormats.includes(value as string)
                ? true
                : `不支持的 TOEFL 听力题型: ${value}，必须是以下之一: ${validFormats.join(', ')}`;
            },
          }
        ),
        // 音频时长（秒）
        audioDuration: createFieldDefinition(
          'audioDuration',
          'number',
          false,
          '音频时长（秒）'
        ),
      },

      constraints: {
        maxAudioDuration: 420, // 单个听力材料约 3-5 分钟，上限 7 分钟
        supportedFormats: ['mp3', 'wav'],
        requiresTranscript: true, // TOEFL 练习提供原文
      },

      customValidator: (question) => {
        const errors = [];

        // TOEFL 听力音频时长验证
        if (question.audioDuration) {
          if (Number(question.audioDuration) > 420) {
            errors.push({
              field: 'audioDuration',
              message: 'TOEFL 听力单个材料音频不能超过 7 分钟',
              code: 'TOEFL_AUDIO_TOO_LONG',
            });
          }
          if (Number(question.audioDuration) < 30) {
            errors.push({
              field: 'audioDuration',
              message: 'TOEFL 听力音频不能短于 30 秒',
              code: 'TOEFL_AUDIO_TOO_SHORT',
            });
          }
        }

        return errors;
      },
    }
  );

/**
 * TOEFL 口语题定义
 */
export const toeflSpeakingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'toefl_speaking',
    QuestionType.FILL_BLANK, // 口语为开放式作答，用填空类型承载
    'TOEFL 口语题',
    {
      responseMode: 'open_ended',
      extraFields: {
        // 任务编号（Task 1 独立口语，Task 2-4 综合口语）
        task: createFieldDefinition('task', 'number', true, 'Task 编号 (1-4)', {
          validator: (value) => {
            const num = Number(value);
            if (num < 1 || num > 4) {
              return 'TOEFL 口语必须是 Task 1-4';
            }
            return true;
          },
        }),
        // 任务类型
        taskType: createFieldDefinition('taskType', 'string', true, '口语任务类型', {
          validator: (value) => {
            const validTypes = [
              'independent', // 独立口语（Task 1）
              'integrated', // 综合口语（Task 2-4）
            ];
            return validTypes.includes(value as string)
              ? true
              : `不支持的 TOEFL 口语任务类型: ${value}，必须是以下之一: ${validTypes.join(', ')}`;
          },
        }),
      },

      constraints: {
        expectedTaskCount: 4,
      },

      customValidator: (question) => {
        const errors = [];

        // 验证 task 与 taskType 的匹配：Task 1 必须为独立口语，Task 2-4 必须为综合口语
        const task = Number(question.task);
        if (!Number.isNaN(task) && question.taskType != null) {
          if (task === 1 && question.taskType !== 'independent') {
            errors.push({
              field: 'taskType',
              message: 'TOEFL 口语 Task 1 必须是独立口语 (independent)',
              code: 'TOEFL_SPEAKING_TASK_TYPE_MISMATCH',
            });
          }
          if (task >= 2 && task <= 4 && question.taskType !== 'integrated') {
            errors.push({
              field: 'taskType',
              message: 'TOEFL 口语 Task 2-4 必须是综合口语 (integrated)',
              code: 'TOEFL_SPEAKING_TASK_TYPE_MISMATCH',
            });
          }
        }

        return errors;
      },
    }
  );

/**
 * TOEFL 写作题定义
 */
export const toeflWritingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'toefl_writing',
    QuestionType.FILL_BLANK, // 写作为开放式作答，用填空类型承载
    'TOEFL 写作题',
    {
      responseMode: 'open_ended',
      extraFields: {
        // 任务编号（Task 1 综合写作，Task 2 学术讨论写作）
        task: createFieldDefinition('task', 'number', true, 'Task 编号 (1-2)', {
          validator: (value) => {
            const num = Number(value);
            if (num !== 1 && num !== 2) {
              return 'TOEFL 写作必须是 Task 1 或 Task 2';
            }
            return true;
          },
        }),
        // 任务类型
        taskType: createFieldDefinition('taskType', 'string', true, '写作任务类型', {
          validator: (value) => {
            const validTypes = [
              'integrated', // 综合写作（Task 1）
              'academic_discussion', // 学术讨论写作（Task 2）
            ];
            return validTypes.includes(value as string)
              ? true
              : `不支持的 TOEFL 写作任务类型: ${value}，必须是以下之一: ${validTypes.join(', ')}`;
          },
        }),
      },

      constraints: {
        expectedTaskCount: 2,
      },

      customValidator: (question) => {
        const errors = [];

        // 验证 task 与 taskType 的匹配
        const task = Number(question.task);
        if (!Number.isNaN(task) && question.taskType != null) {
          const expectedType = task === 1 ? 'integrated' : 'academic_discussion';
          if (question.taskType !== expectedType) {
            errors.push({
              field: 'taskType',
              message: `TOEFL 写作 Task ${task} 的任务类型必须是 ${expectedType}`,
              code: 'TOEFL_WRITING_TASK_TYPE_MISMATCH',
            });
          }
        }

        return errors;
      },
    }
  );

/**
 * 所有 TOEFL 题目类型定义
 */
export const TOEFL_QUESTION_TYPE_DEFINITIONS: Record<
  string,
  ExamSpecificQuestionTypeDefinition
> = {
  [toeflReadingDefinition.id]: toeflReadingDefinition,
  [toeflListeningDefinition.id]: toeflListeningDefinition,
  [toeflSpeakingDefinition.id]: toeflSpeakingDefinition,
  [toeflWritingDefinition.id]: toeflWritingDefinition,
};

/**
 * 根据基础类型获取 TOEFL 题目类型定义
 */
export function getTOEFLQuestionTypeDefinition(
  baseType: QuestionType
): ExamSpecificQuestionTypeDefinition | undefined {
  return Object.values(TOEFL_QUESTION_TYPE_DEFINITIONS).find(
    (def) => def.baseType === baseType
  );
}

/**
 * 获取 TOEFL 题目类型的 Section 建议
 * 根据题目类型推荐应该属于哪个 Section
 */
export function getTOEFLSectionSuggestion(
  questionTypeId: string
): 'reading' | 'listening' | 'speaking' | 'writing' | null {
  const map: Record<string, 'reading' | 'listening' | 'speaking' | 'writing'> = {
    [toeflReadingDefinition.id]: 'reading',
    [toeflListeningDefinition.id]: 'listening',
    [toeflSpeakingDefinition.id]: 'speaking',
    [toeflWritingDefinition.id]: 'writing',
  };
  return map[questionTypeId] ?? null;
}
